// "Forgot your password?": an emailed link (https://play.<domain>/#reset=<token>) opens the dialog that sets
// a new one, then signs in with it.
import { useEffect, useState } from 'preact/hooks';
import { KeyRound, MailCheck } from 'lucide-preact';
import { api } from '../api/routes';
import { errorMessage } from '../api/http';
import { openModal } from '../state/nav';
import { login } from '../state/session';
import { me } from '../state/store';
import { AsyncButton, confirmDialog, Field, Modal } from '../ui/kit';

const looksLikeEmail = (s: string): boolean => /.+@.+\..+/.test(s.trim());

/**
 * Email links open on phones held upright: these dialogs show over the "turn your phone sideways"
 * screen (nothing here needs landscape).
 */
function useUprightOk(): void {
  useEffect(() => {
    document.body.classList.add('upright-ok');
    return () => document.body.classList.remove('upright-ok');
  }, []);
}

/** Under the login forms. */
export function ForgotPasswordLink(props: { email: string }) {
  return (
    <button type="button" class="linkish forgot-link" onClick={() => openForgotPassword(props.email)}>
      Forgot your password?
    </button>
  );
}

export function openForgotPassword(email = ''): void {
  openModal((close) => <ForgotPassword email={email} close={close} />);
}

function ForgotPassword(props: { email: string; close: () => void }) {
  useUprightOk();
  const [email, setEmail] = useState(props.email);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (sent) {
    return (
      <Modal title="Check your email" icon={<MailCheck size={22} />} onClose={props.close} size="sm">
        <p>
          If <b>{email.trim()}</b> is linked to a marine, a link to choose a new password is on its way. It works for one hour.
        </p>
        <p class="muted small">Nothing after a few minutes? Check your spam folder, or ask again.</p>
        <button class="btn primary big" onClick={props.close}>
          OK
        </button>
      </Modal>
    );
  }
  return (
    <Modal title="Forgot password?" icon={<KeyRound size={22} />} onClose={props.close} size="sm">
      <form class="reset-form" onSubmit={(e) => e.preventDefault()}>
        <p class="muted">Enter the email you linked to your marine. We'll send a link to choose a new password.</p>
        <Field label="Email" error={error}>
          <input class="ui-input" type="email" autoComplete="email" value={email} onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <AsyncButton
          class="primary big"
          disabled={!looksLikeEmail(email)}
          onClick={async () => {
            setError(null);
            try {
              await api.auth.forgotPassword({ email: email.trim() });
              setSent(true);
            } catch (e) {
              setError(errorMessage(e));
            }
          }}
        >
          Send the link
        </AsyncButton>
      </form>
    </Modal>
  );
}

/** The emailed link: #reset=<token> on the game's address. */
export function takeResetToken(): string | null {
  const m = /^#reset=([A-Za-z0-9_-]{20,200})$/.exec(location.hash);
  if (!m) return null;
  // Out of the address bar (and history) right away.
  history.replaceState(null, '', location.pathname + location.search);
  return m[1];
}

export function openResetPassword(token: string): void {
  openModal((close) => <ResetPassword token={token} close={close} />, { dismissable: false });
}

function ResetPassword(props: { token: string; close: () => void }) {
  useUprightOk();
  const [pw, setPw] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const short = pw.length > 0 && pw.length < 8;
  const mismatch = again.length > 0 && again !== pw;
  return (
    <Modal title="New password" icon={<KeyRound size={22} />} onClose={props.close} size="sm">
      <form class="reset-form" onSubmit={(e) => e.preventDefault()}>
        <Field label="New password" hint="At least 8 characters." error={short ? 'At least 8 characters' : null}>
          <input class="ui-input" type="password" autoComplete="new-password" value={pw} onInput={(e) => setPw((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <Field label="Type it again" error={mismatch ? "The passwords don't match" : null}>
          <input class="ui-input" type="password" autoComplete="new-password" value={again} onInput={(e) => setAgain((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        {error && <p class="reset-error">{error}</p>}
        {expired ? (
          <button
            type="button"
            class="btn primary big"
            onClick={() => {
              props.close();
              openForgotPassword();
            }}
          >
            Send me a new link
          </button>
        ) : (
          <AsyncButton
            class="primary big"
            disabled={pw.length < 8 || again !== pw}
            onClick={async () => {
              setError(null);
              let email: string;
              try {
                email = (await api.auth.resetPassword({ token: props.token, password: pw })).email;
              } catch (e) {
                setError(errorMessage(e));
                setExpired(/expired|used/i.test(errorMessage(e)));
                return;
              }
              props.close();
              // A guest marine on this device that isn't linked to an email would be left behind.
              const m = me.value;
              if (m && m.isGuest && !m.email) {
                const go = await confirmDialog({
                  title: 'Password changed',
                  body: "Log in with it now? The guest marine on this device isn't linked to an email: if you switch, you can't come back to it.",
                  confirm: 'Log in',
                });
                if (!go) return;
              }
              await login(email, pw);
            }}
          >
            Save and log in
          </AsyncButton>
        )}
      </form>
    </Modal>
  );
}
